import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { reviver } from '../hydration.js';

//#region Paging

export const pagingSchema = z.object({
  pageIndex: z.coerce.number().optional().default(0),
  pageSize: z.coerce.number().optional(),
});

export type PagingModifier = z.infer<typeof pagingSchema>;

export const allPagesModifier = { pageIndex: 0, pageSize: 10000 } satisfies PagingModifier;

export const typeaheadPagesModifier = { pageIndex: 0, pageSize: 10 } satisfies PagingModifier;

//#endregion

//#region Filtering

export interface FilteringModifier<FilterSet extends object> {
  filters?: FilterSet;
}

export function filteringSchemaFrom<FilterSetZodType extends z.SomeZodObject>(
  filterSetSchema: FilterSetZodType,
): z.ZodObject<{ filters: z.ZodOptional<z.ZodPipeline<AnyJsonValueZodType, FilterSetZodType>> }> {
  return z.object({
    filters: anyJsonValueSchema.pipe(filterSetSchema).optional(),
  });
}

export const typeaheadFilterSetSchema = z.object({
  typeahead: z.string().optional(),
});

//#endregion

//#region Sorting

export const sortingOrderSchema = z.enum(['asc', 'desc']);

export type SortingOrder = z.infer<typeof sortingOrderSchema>;

export interface SortingMethod<Field extends string> {
  field: Field;
  order: SortingOrder;
}

export function sortingMethodFromSpecifier<Field extends string>(
  specifier: SortingSpecifier<Field>,
): SortingMethod<Field> {
  const [field, order] = specifier.split('-');
  return {
    field: field as Field,
    order: order as SortingOrder,
  };
}

export function sortingMethodsOf<Field extends string>(specifiers: SortingSpecifier<Field>[]): SortingMethod<Field>[] {
  return specifiers.map((specifier) => sortingMethodFromSpecifier<Field>(specifier));
}

export type SortingSpecifier<Field extends string> = `${Field}-${SortingOrder}`;

export function sortingSpecifierSchemaFrom<Fields extends [string, ...string[]]>(
  enumSchema: z.ZodEnum<Fields>,
): z.ZodEffects<z.ZodString, SortingSpecifier<Fields[number]>> {
  return z.string().superRefine((input, ctx): input is SortingSpecifier<Fields[number]> => {
    const [field, sortingOrder, third] = input.split('-');
    if (!field || !sortingOrder || Boolean(third)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Sorting specifier must follow the format `${fieldName}-${sortingOrder}`',
      });
      return z.NEVER;
    }

    const fieldResult = enumSchema.safeParse(field);
    if (!fieldResult.success) {
      for (const issue of fieldResult.error.issues) {
        ctx.addIssue(issue);
      }
    }

    const sortingOrderResult = sortingOrderSchema.safeParse(sortingOrder);
    if (!sortingOrderResult.success) {
      for (const issue of sortingOrderResult.error.issues) {
        ctx.addIssue(issue);
      }
    }

    return true;
  });
}

export interface SortingModifier<Field extends string> {
  sortBy?: SortingSpecifier<Field>[];
}

export function sortOrderSchemaFrom<Fields extends [string, ...string[]]>(
  enumSchema: z.ZodEnum<Fields>,
): z.ZodArray<z.ZodEffects<z.ZodString, SortingSpecifier<Fields[number]>>> {
  return z.array(sortingSpecifierSchemaFrom(enumSchema));
}

export function sortingSchemaFrom<Fields extends [string, ...string[]]>(
  enumSchema: z.ZodEnum<Fields>,
): z.ZodObject<{
  sortBy: z.ZodOptional<
    z.ZodPipeline<AnyJsonValueZodType, z.ZodArray<z.ZodEffects<z.ZodString, SortingSpecifier<Fields[number]>>>>
  >;
}> {
  const sortOrderSchema = sortOrderSchemaFrom(enumSchema);

  return z.object({
    sortBy: anyJsonValueSchema.pipe(sortOrderSchema).optional(),
  });
}

//#endregion

//#region Utilities

const anyJsonValueSchema = z.string().transform((s, ctx) => {
  try {
    return JSON.parse(s, reviver);
  } catch (e: unknown) {
    if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: e.message,
      });
    }
    return z.NEVER;
  }
});
type AnyJsonValueZodType = typeof anyJsonValueSchema;

export type QueryStringPrimitive =
  | string
  | number
  | boolean
  | Temporal.PlainDate
  | Temporal.PlainTime
  | Temporal.Instant
  | Temporal.Duration;

export type QueryStringValueSerializable =
  | QueryStringPrimitive
  | Array<QueryStringPrimitive>
  | Record<string, QueryStringPrimitive>;

export function toQueryStringValue<T extends QueryStringValueSerializable>(x: T): string {
  if (typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean') {
    return x.toString();
  }
  if (!(typeof x === 'object')) {
    throw new TypeError('Cannot serialize into query string value');
  }

  if (
    x instanceof Temporal.PlainDate ||
    x instanceof Temporal.PlainTime ||
    x instanceof Temporal.Instant ||
    x instanceof Temporal.Duration
  ) {
    return x.toString();
  }

  if (Array.isArray(x)) {
    return JSON.stringify(x);
  }

  return JSON.stringify(x);
}

//#endregion
